import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { TextField, Button, CircularProgress } from "@mui/material";
import {
    ContactMaster,
    SortingOptions,
    ContactDescription,
    UserMaster,
} from "../types";
import { getImageUrl } from "../utils";
import Image from "./image";
import { theme } from "../theme";
import debounce from "debounce";
import { ReactComponent as XCircle } from "../assets/x-circle.svg";
import { ReactComponent as NetworkIcon } from "../assets/network.svg";
import { connect } from "react-redux";
import { RootState } from "../redux/root-store";
import dayjs from "dayjs";
import {
    getProfileFollowersListener,
    removeRecipientContact,
} from "../firebase";
import Confirm from "./confirm";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: stretch;
    @media only screen and (max-width: 720px) {
    }
`;

const Wrapper = styled(Container)`
    //border-right: 1px solid #ccc;
    @media only screen and (max-width: 720px) {
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        padding-top: 1em;
        border-right: 0;
    }
`;

const Row = styled.div`
    width: 100%;
    display: flex;
`;

const SplitRow = styled(Row)`
    & > * {
        flex: 1;
        flex-grow: 1;
    }
`;

const ContactRow = styled(Row)`
    align-items: center;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 80%;
    margin: 0 auto;
    &:hover {
        background-color: ${theme.palette.grey2.light};
    }
`;

const ContactTop = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 70px;
`;

const ContactImage = styled(Image)`
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    margin-right: 1rem;
`;
const ContactName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    font-size: 1.2rem;
    flex: 1;
    & > b {
        color: ${theme.palette.primary.main};
    }
`;
const FactMatch = styled("span")(
    ({ theme, $isLast }: any) => `
    color: ${theme.palette.grey2.main};
    margin-left: 7rem;
    margin-right: 3.5rem;
    width: calc(100% - 3.5em);
    ${(!$isLast && `border-bottom: 1px solid #ccc;`) || ""}
    & > b {
        color: ${theme.palette.primary.main};
    }
`
);

const mapDispatchToProps = {};
const mapStateToProps = (state: RootState) => ({
    loggedUser: state.session.user,
});
type dispatchType = typeof mapDispatchToProps;

interface Props extends dispatchType, ReturnType<typeof mapStateToProps> {
    containerProps?: React.CSSProperties;
}

const ProfileFollowers = (props: Props) => {
    const { loggedUser } = props;
    const [followers, setFollowers] = useState<UserMaster[]>([]);
    const [deleteFollower, setDeleteFollower] = useState<UserMaster | null>(
        null
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!loggedUser) return;
        const unsubscribeFollowers = getProfileFollowersListener(
            loggedUser.user_id,
            (followers: UserMaster[]) => {
                setFollowers(followers);
                setIsLoading(false);
            }
        );
        return () => {
            unsubscribeFollowers();
        };
    }, []);

    return (
        <Wrapper
            style={{
                height: "100%",
                ...props.containerProps,
            }}
        >
            <Container style={{ width: "80%", margin: "0 auto" }}>
                <Row style={{ marginTop: "0.5em" }}>Sharing with</Row>
            </Container>
            <Container
                style={{
                    flexGrow: 1,
                    overflow: "auto",
                    paddingBottom: "2em",
                    alignItems: "flex-start",
                }}
            >
                {followers.map((follower: UserMaster) => (
                    <ContactRow key={follower.user_id}>
                        <ContactTop>
                            <ContactImage
                                src={getImageUrl(follower.profile || "")}
                            />
                            <ContactName>{follower.name}</ContactName>
                            <span
                                style={{
                                    fontWeight: "bold",
                                    marginRight: "0.5rem",
                                    cursor: "pointer",
                                }}
                                onClick={() => setDeleteFollower(follower)}
                            >
                                X
                            </span>
                        </ContactTop>
                    </ContactRow>
                ))}

                {isLoading && (
                    <Row
                        style={{ justifyContent: "center", margin: "50px 0px" }}
                    >
                        <CircularProgress />
                    </Row>
                )}
            </Container>
            {deleteFollower && (
                <Confirm
                    open={true}
                    confirmText="Delete"
                    onCancel={() => setDeleteFollower(null)}
                    onConfirm={() => {
                        if (!props.loggedUser) return;
                        if (!deleteFollower) return;
                        removeRecipientContact(
                            props.loggedUser.user_id,
                            deleteFollower.user_id
                        );
                        setDeleteFollower(null);
                    }}
                />
            )}
        </Wrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileFollowers);
